import { mapFilterSlugsToIds, useFilterSearch, useUrlFilter, type MappedFilterValues } from "@/composables/filter";
import { Sort } from "@/composables/marketplace/marketplaceFilter";
import { searchCollectionItemsElastic, type CollectionItemElastic } from "@/services/collection/item/elastic";
import type { CategoryId } from "@/services/collection/categories";
import { fetchAllCategories } from "@/services/collection/categories";
import type { FilterDefinition, SortDefinition } from "@/services/filter";

export const DEFAULT_PER_PAGE = 60;

export const QUERY_FILTER_SLUG = "query";
export const CATEGORY_FILTER_SLUG = "category";
export const SORT_FILTER_SLUG = "sort";

type MarketplaceSearchRequest = {
  page: number;
  perPage: number;
  sellable: boolean;
  filters: MappedFilterValues;
  query?: string;
  categoryId?: CategoryId;
  sort?: string;
};

/**
 * Marketplace-specific search composable.
 * Uses useUrlFilter() + useFilterSearch() with domain-specific API calls.
 */
export function useMarketplaceSearch(perPage: number = DEFAULT_PER_PAGE) {
  const { filterValues, page, setFilter, setFilters, setRangeFilter, setPage, resetFilters } = useUrlFilter();

  const apiFilterDefinitions = ref<FilterDefinition[]>([]);
  const sortDefinition = ref<SortDefinition | null>(null);

  // Combine hardcoded filters with API filter definitions (query is rendered separately)
  const filterDefinitions = computed<FilterDefinition[]>(() => [...apiFilterDefinitions.value]);

  // Computed trigger that combines all reactive dependencies
  const searchTrigger = computed(() => ({
    filterValues: filterValues.value,
    page: page.value,
  }));

  // Track if we have URL filters on first load (before definitions are available)
  const hadFiltersOnFirstLoad = Object.keys(filterValues.value).length > 0;

  const { items, totalItems, isLoading, error, refetch } = useFilterSearch<CollectionItemElastic, MarketplaceSearchRequest>(searchTrigger, {
    perPage,
    buildRequest: async (filters, currentPage, itemsPerPage) => {
      // Extract query value before mapping (it's not in API definitions)
      const query = typeof filters[QUERY_FILTER_SLUG] === "string" ? filters[QUERY_FILTER_SLUG] : undefined;

      // Extract category value before mapping (it's not in API definitions)
      // Await fetchAllCategories to ensure categories are available (cached after first call)
      const categorySlug = typeof filters[CATEGORY_FILTER_SLUG] === "string" ? filters[CATEGORY_FILTER_SLUG] : undefined;
      let categoryId: CategoryId | undefined;
      if (categorySlug) {
        const categories = await fetchAllCategories();
        categoryId = categories.find((cat) => cat.url === categorySlug)?.id;
      }

      // Extract sort value before mapping (default to DateDesc to match UI default)
      const sort = typeof filters[SORT_FILTER_SLUG] === "string" ? filters[SORT_FILTER_SLUG] : Sort.DateDesc;

      // Remove hardcoded filters before mapping to IDs
      const filtersForMapping = { ...filters };
      delete filtersForMapping[QUERY_FILTER_SLUG];
      delete filtersForMapping[CATEGORY_FILTER_SLUG];
      delete filtersForMapping[SORT_FILTER_SLUG];

      const filtersRemaped = mapFilterSlugsToIds(filtersForMapping, apiFilterDefinitions.value);

      return {
        page: currentPage,
        perPage: itemsPerPage,
        sellable: true,
        filters: filtersRemaped,
        query: query || undefined,
        categoryId,
        sort,
      };
    },
    executeSearch: async (request, options) => {
      const response = await searchCollectionItemsElastic(request, options);

      apiFilterDefinitions.value = response.parameters;
      // sortDefinition.value = response.sort; @todo Not implemented in API yet
      return {
        items: response.items,
        totalItems: response.totalItems,
      };
    },
  });

  // When filter definitions are first loaded and we had URL filters, refetch with proper mapping
  watch(apiFilterDefinitions, (definitions, oldDefinitions) => {
    if (definitions.length > 0 && (!oldDefinitions || oldDefinitions.length === 0) && hadFiltersOnFirstLoad) {
      refetch();
    }
  });

  // Compute active filter count based on known filter definitions
  const activeFilterCount = computed(() => {
    let count = 0;

    // Count query filter if set
    const queryValue = filterValues.value[QUERY_FILTER_SLUG];
    if (queryValue && queryValue !== "") {
      count++;
    }

    // Count API filters
    if (apiFilterDefinitions.value.length > 0) {
      const filterFields = new Set(apiFilterDefinitions.value.map((f) => f.slug));

      for (const [key, value] of Object.entries(filterValues.value)) {
        // Skip sort field and hardcoded filters (already counted)
        if (sortDefinition.value && key === sortDefinition.value.field) continue;
        if (key === QUERY_FILTER_SLUG) continue;
        if (key === CATEGORY_FILTER_SLUG) continue;
        if (key === SORT_FILTER_SLUG) continue;

        // Check if this is a known filter field (handle range _min/_max)
        const baseField = key.replace(/_min$|_max$/, "");
        if (filterFields.has(baseField) && value !== undefined) {
          if (Array.isArray(value) ? value.length > 0 : value !== "") {
            count++;
          }
        }
      }
    }

    return count;
  });

  return {
    filterValues,
    page,
    setFilter,
    setFilters,
    setRangeFilter,
    setPage,
    resetFilters,
    items,
    totalItems,
    filterDefinitions,
    sortDefinition,
    isLoading,
    error,
    activeFilterCount,
    refetch,
    perPage,
  };
}
